/**
 * Start work from anywhere (slice 3.2), on the Processing tab: the "on"
 * picker (automatic, or a computer that can do the work), the whole job's
 * button, and the per-capture "Process" button's handler. It works the same
 * on a phone (one picker for the tab, 44 px targets, nothing wider than the
 * screen). The server decides everything (functions
 * handlers/processing/startWork.ts); the words come from
 * src/lib/startWorkModel.js.
 *
 * useStartWork(jobId) holds the options (getWorkOptions, read again every 30
 * seconds with the queue and after every press), the chosen computer, what
 * is being pressed, and each capture's last answer. A press sends its own
 * request id, and its button is off until the answer comes, so a double tap
 * starts nothing twice.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { work } from "../../../../lib/workCallables.js";
import {
  AUTOMATIC, jobResultText, captureResultText, labelIn, pickerOf, pressIds, pressKey, processLabel, validTarget,
} from "../../../../lib/startWorkModel.js";

export function useStartWork(jobId, { onDone, onRowPress } = {}) {
  const [options, setOptions] = useState(null);
  const [target, setTargetRaw] = useState(AUTOMATIC);
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState({});
  const [banner, setBanner] = useState(null);
  const live = useRef(true);
  const pressing = useRef(false);
  // The job on screen now: an answer about another job (the tab moved on) is never shown here.
  const currentJob = useRef(jobId);
  const stillHere = (forJob) => live.current && currentJob.current === forJob;

  const loadOptions = useCallback(async () => {
    if (!jobId) return;
    try {
      const o = await work.getWorkOptions({ jobId });
      if (live.current && currentJob.current === jobId) setOptions(o);
    } catch {
      // Without the options the tab still works; the start buttons wait for the next read.
    }
  }, [jobId]);
  useEffect(() => {
    live.current = true;
    currentJob.current = jobId;
    setOptions(null);
    setNotes({});
    setBanner(null);
    loadOptions();
    return () => { live.current = false; };
  }, [jobId, loadOptions]);

  const effective = validTarget(options, target);
  const labelOf = labelIn(options);

  /** One press at a time: a second tap while the first is on its way does nothing. */
  const press = async (key, fn) => {
    if (pressing.current) return;
    pressing.current = true;
    setBusy(key);
    try {
      await fn();
    } finally {
      pressing.current = false;
      if (live.current) setBusy(null);
      loadOptions();
      onDone?.();
    }
  };

  // A press whose answer is unknown keeps its request id (pressIds, for the whole page), so pressing
  // again, here or after leaving the tab, asks about the same press and never does it twice.
  const startOne = (row) => press(`start:${row.taskId}`, async () => {
    const forJob = jobId;
    setNotes((n) => ({ ...n, [row.taskId]: null }));
    onRowPress?.(row.taskId);
    const key = pressKey(forJob, row.sn, effective);
    const requestId = pressIds.idFor(key);
    try {
      const r = await work.startWork({ kind: "process", jobId: forJob, sn: row.sn, machineUid: effective || undefined, requestId });
      pressIds.settle(key, requestId);
      if (stillHere(forJob)) setNotes((n) => ({ ...n, [row.taskId]: { kind: "ok", text: captureResultText(r, labelOf) } }));
    } catch (e) {
      pressIds.settle(key, requestId, e);
      if (stillHere(forJob)) setNotes((n) => ({ ...n, [row.taskId]: { kind: "error", text: e?.message || "That did not work. Try again." } }));
    }
  });

  const startJob = () => press("startJob", async () => {
    const forJob = jobId;
    setBanner(null);
    const key = pressKey(forJob, null, effective);
    const requestId = pressIds.idFor(key);
    try {
      const r = await work.startWork({ kind: "process", jobId: forJob, machineUid: effective || undefined, requestId });
      pressIds.settle(key, requestId);
      if (stillHere(forJob)) setBanner({ kind: "info", text: jobResultText(r, labelOf) });
    } catch (e) {
      pressIds.settle(key, requestId, e);
      if (stillHere(forJob)) setBanner({ kind: "error", text: e?.message || "That did not work. Try again." });
    }
  });

  return {
    options, target: effective, setTarget: setTargetRaw, busy, notes, banner, dismissBanner: () => setBanner(null),
    labelOf, startOne, startJob, reload: loadOptions,
    clearNote: (taskId) => setNotes((n) => ({ ...n, [taskId]: null })),
  };
}

/** The "on" picker: automatic, or a computer the server offers. One for the whole tab. */
export function OnPicker({ options, value, onChange, disabled = false, id = "start-on" }) {
  const { choices, notOffered } = pickerOf(options);
  return (
    <div className="sw-on">
      <label className="sw-on-row" htmlFor={id}>
        <span className="rv-label">On</span>
        <select id={id} className="rv-input sw-select" value={value} disabled={disabled || !options} onChange={(e) => onChange(e.target.value)}>
          {choices.map((c) => <option key={c.value || "auto"} value={c.value}>{c.label}</option>)}
        </select>
      </label>
      {notOffered.length > 0 && (
        <span className="rv-muted sw-not" data-testid="not-offered">
          Not offered: {notOffered.map((m) => `${m.label} (${m.why})`).join("; ")}.
        </span>
      )}
    </div>
  );
}

/**
 * The Processing tab's "Start work" card: the picker and the whole job's
 * button. Pure props (the tab passes useStartWork's state).
 */
export function StartWorkCard({ start, jobListed, paused, onAskJob }) {
  const { options, target, setTarget, busy, labelOf } = start;
  const startable = (options?.kinds ?? []).includes("process");
  return (
    <div className="pp-switch sw-card">
      <div className="pp-switch-main">
        <span className="rv-label">Start work</span>
      </div>
      <span className="rv-muted">
        {target
          ? `A capture you press "Process" on is held for ${labelOf(target)} for 10 minutes, and it takes it next.`
          : "A capture you press \"Process\" on moves to the front; the best free computer takes it next."}
      </span>
      <OnPicker options={options} value={target} onChange={setTarget} disabled={Boolean(busy)} />
      {!jobListed && <span className="rv-line warn">Switch this job on first: until then nothing of it is processed.</span>}
      {jobListed && paused && <span className="rv-line">Processing is paused: started captures wait until it resumes.</span>}
      <button type="button" className="rigv2-btn small primary rv-primary" disabled={Boolean(busy) || !startable || !jobListed} onClick={onAskJob}>
        {busy === "startJob" ? "Starting…" : target ? `Process this job's captures on ${labelOf(target)}` : "Process this job's captures"}
      </button>
    </div>
  );
}

/** A queued capture's own button (the row of the Processing tab). */
export function ProcessRowButton({ start, row }) {
  if (row.state !== "queued" || (row.kind && row.kind !== "process") || !(start.options?.kinds ?? []).includes("process")) return null;
  const mine = start.busy === `start:${row.taskId}`;
  return (
    <button type="button" className="rigv2-btn small primary rv-primary" disabled={Boolean(start.busy)} onClick={() => start.startOne(row)}>
      {mine ? "Starting…" : processLabel(start.target, start.labelOf)}
    </button>
  );
}
